// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import './Login.scss';
import { useEffect, useState } from 'react';
import { createAxios } from 'ts/createAxios';
import { LoginTemplate } from 'components/templates/LoginTemplate';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setUser, resetUser } from 'redux/slices/userSlice';
import {
  setIsCheckingSignin,
  setIsSignin,
  setPathAfterSignin,
  resetPath,
} from 'redux/slices/signinSlice';
import { resetHistory } from 'redux/slices/historySlice';

import { firstPage } from 'routes';
import { useTranslation } from 'react-i18next';
import { TextInputFieldLogin } from 'components/molecules/TextInputFieldLogin';
import { validateRequiredField } from 'util/validator';
import { IconLanguage } from 'components/atoms/icons/IconLanguage';
import { SelectField } from 'components/molecules/SelectField';
import {
  LanguageTypeForm,
  LanguageValue,
  languageTypeData,
} from 'util/ConstantValues';
import i18n from 'i18n';
import { resetSiteInfo } from 'redux/slices/siteInfoSlice';
import { resetCategoryWasteUnit } from 'redux/slices/categoryWasteUnitSlice';
import { DbManager } from 'util/DbManager';
import CookiesAuth from 'util/CookiesAuth';
import ConfirmMFAModal from 'components/organisms/ConfirmMFAModal';
import { handleError } from 'util/errorHandler';
import { AxiosError, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { calculateMfaWaitTimeFromMessage } from 'util/commons';
import { useAuthenLockTime } from 'util/hooks/useAuthenLockTime';
import dayjs from 'dayjs';
import {
  getSavedMfaSession,
  removeSavedMfaSession,
  setSavedMfaSession,
} from 'util/mfa';
import { resetHistoriesExport } from 'redux/slices/historiesCsvExportSlice';

type LoginInput = {
  userId: string;
  password: string;
};

const Login = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [isError, setIsError] = useState<boolean>(false);
  const langLocalStorage = localStorage.getItem('i18nextLng') as LanguageValue;
  const [mfaData, setMfaData] = useState<{
    userId: string;
    password: string;
    session: string;
    email: string;
    error?: string;
    defaultResendCountDown?: number;
  }>();

  useEffect(() => {
    // アイテムをリセット
    localStorage.removeItem('scale-login');
    CookiesAuth.logout();
    DbManager.userInfo.clear();
    window.nativeUpdateUser(null);

    dispatch(setIsCheckingSignin(false));
    dispatch(setIsSignin(false));
    dispatch(resetSiteInfo());
    dispatch(resetCategoryWasteUnit());
    dispatch(resetUser());
    dispatch(resetHistory());
    dispatch(resetPath());
    dispatch(resetHistoriesExport());
  }, []);

  const [selectLanguage, setSelectLanguage] = useState<LanguageTypeForm>({
    language: LanguageValue.Japanese,
  });

  useEffect(() => {
    if (langLocalStorage) {
      setSelectLanguage({ language: langLocalStorage });
    } else {
      i18n.changeLanguage(LanguageValue.Japanese);
    }
  }, []);

  const { updateLock, setCheckLockUserId, countdown, message } =
    useAuthenLockTime();

  const handleGetAccount = () => {
    // クッキー設定後にもう一度axiosのインスタンスを生成する
    const myAccountApi = createAxios({
      mode: 'login',
    });
    // ユーザ情報取得を呼ぶ
    myAccountApi
      .get('/auth/my-account')
      .then((res) => {
        const redirect = firstPage(res.data);

        DbManager.updateUserInfo(res.data);
        window.nativeUpdateUser(res.data);

        dispatch(setIsSignin(true));
        dispatch(setUser(res.data));
        dispatch(setPathAfterSignin(redirect));

        history.push(redirect);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleLoginSucceed = (params: {
    res: AxiosResponse;
    userId: string;
    password: string;
    isVerify?: boolean;
  }) => {
    const { res, userId, password, isVerify } = params;

    if (res.data.mfaEnable) {
      setMfaData({
        ...mfaData,
        userId,
        password,
        session: res.data.session,
        email: res.data.email || mfaData?.email,
        defaultResendCountDown: 60,
        error: isVerify
          ? t('confirm_mfa_modal.verify_failed') || ''
          : undefined,
      });

      setSavedMfaSession(userId, {
        session: res.data.session,
        email: res.data.email || mfaData?.email,
        expiresAt: dayjs().add(1, 'minute').toString(),
      });

      return;
    }

    if (res.data.IdToken && res.data.RefreshToken) {
      setMfaData(undefined);
      removeSavedMfaSession(userId);

      CookiesAuth.login({
        accessToken: res.data.IdToken,
        refreshToken: res.data.RefreshToken,
      });
    }

    handleGetAccount();
  };

  const handleLogin = async (values: LoginInput, isResendMfa?: boolean) => {
    dispatch(setIsCheckingSignin(true));
    setIsError(false);

    if (!isResendMfa) {
      setMfaData(undefined);
    }

    await createAxios({
      mode: 'login',
      prefix: 'v2',
    })
      .post('/auth/login', {
        userId: values.userId,
        password: values.password,
      })
      .then(function (res) {
        handleLoginSucceed({
          res,
          userId: values.userId,
          password: values.password,
        });

        if (res.data.mfaEnable && isResendMfa) {
          toast.success(t('confirm_mfa_modal.resend_code_succeed'));
        }
      })
      .catch((e: AxiosError) => {
        const mfaWaitTime = calculateMfaWaitTimeFromMessage(
          e.response?.data.message
        );

        if (mfaWaitTime) {
          const savedMfaSession = getSavedMfaSession(values.userId);

          if (savedMfaSession?.session) {
            setMfaData({
              userId: values.userId,
              password: values.password,
              session: savedMfaSession.session,
              email: savedMfaSession.email,
              defaultResendCountDown: mfaWaitTime,
            });
          } else {
            updateLock(values.userId, {
              lockEndTime: dayjs().add(mfaWaitTime, 'seconds').toString(),
              message: 'confirm_mfa_modal.please_wait_before_code_expired',
            });
          }

          return;
        }

        if (e.response?.data.endTimeLock) {
          updateLock(values.userId, {
            lockEndTime: e.response.data.endTimeLock,
            message: 'login.failed_too_many_times',
          });

          return;
        }

        if (e.response?.status === 401 && !isResendMfa) {
          setIsError(true);
        } else {
          handleError(e);
        }
      })
      .finally(() => {
        dispatch(setIsCheckingSignin(false));
      });
  };

  const handleConfirmMFA = (code: string) => {
    if (mfaData) {
      createAxios({
        mode: 'login',
      })
        .post('/auth/verify', {
          userId: mfaData.userId,
          session: mfaData.session,
          otp: code,
        })
        .then((res) => {
          handleLoginSucceed({
            res,
            userId: mfaData.userId,
            password: mfaData.password,
            isVerify: true,
          });
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 401) {
            setMfaData({
              ...mfaData,
              error: t('confirm_mfa_modal.verify_failed') || '',
            });
          } else {
            handleError(error);
          }
        });
    }
  };

  const handleResendMFA = async () => {
    if (mfaData) {
      await handleLogin(
        {
          userId: mfaData.userId,
          password: mfaData.password,
        },
        true
      );
    }
  };

  return (
    <LoginTemplate>
      <div className="logo">
        <img src="/assets/images/logo.svg" alt="logo" />
      </div>
      <Form<LoginInput>
        onSubmit={(data) => handleLogin(data)}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, invalid, values }) => {
          setCheckLockUserId(values.userId);

          if (countdown) {
            setIsError(false);
          }

          return (
            <form onSubmit={handleSubmit}>
              <div className="form-wraper">
                <TextInputFieldLogin
                  validate={(id) => {
                    return validateRequiredField(
                      id,
                      t('login.user_id') || 'ユーザーID'
                    )
                      ? (t) =>
                          validateRequiredField(
                            id,
                            t('login.user_id') || 'ユーザーID'
                          )
                      : undefined;
                  }}
                  label={t('login.user_id') || 'ユーザーID'}
                  name={'userId'}
                  icon="user"
                  placeholder={t('login.user_id') || 'ユーザーID'}
                />
                <TextInputFieldLogin
                  label={t('login.password') || 'パスワード'}
                  name={'password'}
                  type={'password'}
                  icon="password"
                  placeholder={t('login.password') || 'パスワード'}
                  validate={(password) => {
                    return validateRequiredField(
                      password,
                      t('login.password') || ''
                    )
                      ? (t) =>
                          validateRequiredField(
                            password,
                            t('login.password') || ''
                          )
                      : undefined;
                  }}
                />

                {isError && (
                  <div className="errors">
                    {t('login.input_error')}
                    <br />
                    {t('login.message_error')}
                  </div>
                )}

                {message && Boolean(countdown) && (
                  <div className="errors">{t(message)}</div>
                )}

                <button
                  disabled={invalid || !!countdown}
                  type="submit"
                  onClick={handleSubmit}
                  style={{ margin: '16px 0 32px 0' }}
                >
                  {countdown || t('login.button')}
                </button>
                <p>{t('login.notice_1')}</p>
                {selectLanguage.language === LanguageValue.Japanese ? (
                  <p>
                    <a href="mailto:info@recotech.co.jp" target="_blank">
                      info@recotech.co.jp
                    </a>
                    {t('login.notice_2')}
                  </p>
                ) : (
                  <p>
                    {t('login.notice_2')}
                    <a href="mailto:info@recotech.co.jp" target="_blank">
                      info@recotech.co.jp.
                    </a>
                  </p>
                )}
              </div>
            </form>
          );
        }}
      </Form>
      <div className="login-language">
        <Form<LanguageTypeForm>
          onSubmit={() => {}}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          initialValues={selectLanguage}
        >
          {(props) => {
            const changeLanguage = (value) => {
              value === LanguageValue.Japanese
                ? i18n.changeLanguage(LanguageValue.Japanese)
                : i18n.changeLanguage(LanguageValue.English);
              props.form.mutators.setValue('language', value);
              setSelectLanguage({ language: value });
            };

            return (
              <form onSubmit={props.handleSubmit}>
                <div className="login-selectLanguage">
                  <div className="icon-language">
                    <IconLanguage />
                  </div>

                  <SelectField
                    name="language"
                    placeholder="Language"
                    options={languageTypeData}
                    value={props.values.language}
                    onChange={changeLanguage}
                    isDarkmode
                    isLanguage
                  />
                </div>
              </form>
            );
          }}
        </Form>
      </div>

      {mfaData && (
        <ConfirmMFAModal
          {...mfaData}
          onClose={() => setMfaData(undefined)}
          onSubmit={handleConfirmMFA}
          onResendCode={handleResendMFA}
        />
      )}
    </LoginTemplate>
  );
};

export default Login;
